import React, { useState, useEffect, useCallback } from "react";
import "../styles.scss";
import { Document, Page, pdfjs } from "react-pdf";
import { getFieldConfig, getFieldSettings } from "../../../components/CustomDocumentation/utils/fieldConfig";
import Loader from "../../../components/Loader";
import FieldRenderer from "../../../components/CustomDocumentation/FieldRenderer";
import Header from "../../../components/CustomDocumentation/Header";
import StickyMenu from "../../../components/CustomDocumentation/StickyMenu";
import PreviewModal from "../../../components/CustomDocumentation/Modals/PreviewModal";
import { useParams } from "react-router-dom";
import { getData } from "../../../api";
import "./styles.scss";

// Set up the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ViewPage = () => {
  let { id } = useParams();
  const [documentData, setDocumentData] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [placedFields, setPlacedFields] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [modalSettingsObj] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handlePreview = () => {
    setShowPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
  };

  const handleZoomIn = () => {
    if (scale < 1.6) {
      setScale(Math.min(1.6, scale + 0.1));
    }
  };

  const handleZoomOut = () => {
    if (scale > 0.6) {
      setScale(Math.max(0.6, scale - 0.1));
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent browser zoom with Ctrl/Cmd + +/- or 0
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "+" || e.key === "-" || e.key === "0" || e.key === "=")
      ) {
        e.preventDefault();
        e.stopPropagation();

        // Handle our custom PDF zoom instead
        if (e.key === "+" || e.key === "=") {
          if (scale < 1.6) {
            setScale(Math.min(1.6, scale + 0.1));
          }
        } else if (e.key === "-") {
          if (scale > 0.6) {
            setScale(Math.max(0.6, scale - 0.1));
          }
        } else if (e.key === "0") {
          setScale(1.0); // Reset to 100%
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [scale]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      setIsPdfLoading(false);
    }, 100);
  }

  function onDocumentLoadError(error) {
    console.error("Error loading PDF:", error);
    setIsPdfLoading(false);
  }

  // Parse settings data to extract field configurations
  const parseSettingsData = useCallback((settingsString) => {
    try {
      const settings = JSON.parse(settingsString);
      const fields = [];

      // Process each page in the settings
      Object.keys(settings).forEach((pageNum) => {
        const pageFields = settings[pageNum];

        // Process each field on this page
        Object.keys(pageFields).forEach((fieldId) => {
          const fieldData = pageFields[fieldId];

          // Create field object with all properties from settings
          const field = {
            id: fieldId,
            pageNumber: parseInt(pageNum),
            type: fieldData.type,
            value: fieldData.value || "",
            placeholder: fieldData.place_holder || "",
            x: fieldData.left || 0,
            y: fieldData.top || 0,
            width: fieldData.width || 100,
            height: fieldData.height || 30,
            fontSize: fieldData.font_size,
            fontStyle: fieldData.font_style || "Lato",
            characterLimit: fieldData.character_limit,
            scale: fieldData.scale || 1,
            owner: fieldData.owner || "admin",
            needed: fieldData.needed || false,
            options: fieldData.options || [],
            multiple_options: fieldData.multiple_options || false,
          };

          // Add input type and map API field types to menuArray names
          const fieldTypeMapping = {
            text: "Text input",
            textarea: "Text mulit line input",
            date: "Date",
            select: "Dropdown",
            checkbox: "Checkbox",
            radio: "Radio",
            initials: "Initials",
            signature: "Signature",
          };

          // Map the API type to the correct menuArray name
          const mappedType = fieldTypeMapping[fieldData.type] || fieldData.type;

          field.type = mappedType;
          field.inputType = fieldData.type;

          fields.push(field);
        });
      });

      return fields;
    } catch (error) {
      console.error("Error parsing settings:", error);
      return [];
    }
  }, []);

  const getDocumentById = useCallback(
    async (id) => {
      const res = await getData(`custom-client-document/${id}`);
      if (res.status === true) {
        setDocumentData(res.data);
        setDocumentName(res.data.name);
        setIsPdfLoading(true); // Reset loading state for new document

        // Parse settings if available
        if (res.data.settings) {
          const fields = parseSettingsData(res.data.settings);
          setPlacedFields(fields);
        } else {
          setPlacedFields([]);
        }
      }
    },
    [parseSettingsData],
  );

  useEffect(() => {
    if (id !== undefined) {
      getDocumentById(id);
    }
  }, [id, getDocumentById]);

  return (
    <div className="custom-client-document view-mode">
      <Header
        onClearFields={() => {}}
        hasFields={placedFields.length > 0}
        documentData={documentData}
        documentName={documentName}
        setDocumentName={() => {}}
        placedFields={placedFields}
        canvasSettings={{ scale }}
        modalSettingsObj={modalSettingsObj}
        hasUnsavedChanges={() => false}
        onSaveSuccess={() => {}}
        onPreviewClose={() => {}}
        viewMode={true}
        onPreview={handlePreview}
      />
      <div className="custom-client-document-content">
        <div className="left-column-wrapper">
          <StickyMenu
            enhancedMenuArray={[]} 
            activeMenuIndex={null}
            activeSubMenuIndex={null}
            dropdownVisible={false}
            pageNumber={pageNumber}
            numPages={numPages}
            scale={scale}
            handleMenuClick={() => {}} // Disabled in view mode
            setPageNumber={setPageNumber}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
            viewMode={true} 
          />
        </div>
        <div className="right-column-wrapper">
          <div className="pdf-canvas-container">
            <div className="pdf-page-wrapper">
              {documentData?.design_url ? (
                <Document
                  file={`${documentData?.design_url}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<Loader />}
                  error={null}
                  noData={null}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="pdf-page"
                  />
                </Document>
              ) : (
                <Loader />
              )}
              {!isPdfLoading && (
                <FieldRenderer
                  fields={placedFields}
                  pageNumber={pageNumber}
                  scale={scale}
                  modalSettingsObj={modalSettingsObj}
                  getFieldConfig={getFieldConfig}
                  getFieldSettings={getFieldSettings}
                  handleMouseDown={() => {}} // Disabled in view mode
                  handleDuplicateField={() => {}} // Disabled in view mode
                  handleSettingsClick={() => {}} // Disabled in view mode
                  handleDeleteField={() => {}} // Disabled in view mode
                  handleFieldChange={() => {}} // Disabled in view mode
                  handleProvideESignature={() => {}} // Disabled in view mode
                  placedFields={placedFields}
                  setPlacedFields={() => {}} // Disabled in view mode
                  isViewMode={true} // Add view mode flag
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <PreviewModal
        show={showPreviewModal}
        onHide={handleClosePreviewModal}
        documentData={documentData}
        designUrl={
          documentData?.design_url ? `${documentData.design_url}` : null
        }
        placedFields={placedFields}
        modalSettingsObj={modalSettingsObj}
        scale={scale}
      />
    </div>
  );
};

export default ViewPage;
